import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Link,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue,
  VStack,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import SidebarWithHeader from 'components/SidebarWithHeader';
import { FaWhatsapp } from 'react-icons/fa';
import { FiCopy } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { useGetWhatsAppNumbersQuery } from 'store/aiHub/externalAiApi';

const FLOW_BASE_URL = process.env.REACT_APP_FLOW_BASE_URL;

function WhatsAppBot() {
  const userInfo = useSelector((s) => s.auth?.userInfo);
  const bgColor = useColorModeValue('white', 'gray.800');
  const subtle = useColorModeValue('gray.600', 'gray.300');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const textColorSub = useColorModeValue("brand.300", "brand.700");

  const email = userInfo?.email;

  const { data, isFetching, isError } = useGetWhatsAppNumbersQuery(
    email ?? skipToken
  );

  const phoneNumbers = data?.phone_numbers ?? [];
  const purchasedCount = data?.count ?? 0;

  // Track which number is copied for inline feedback
  const [copiedNum, setCopiedNum] = useState(null);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedNum(text);
      // Auto-reset after 2 seconds
      setTimeout(() => setCopiedNum(null), 2000);
    } catch {
      // Optional: you can log or keep silent; avoiding toast per request
      console.error('Failed to copy number');
    }
  };

  return (
    <SidebarWithHeader>
      <Box p={4}>
        {/* Header */}
        <Flex
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'flex-start', md: 'flex-start' }}
          justify="space-between"
          gap={4}
          mb={6}
        >
          {/* Left content gets the flexible space */}
          <Box flex="1" minW={0}>
            <Heading size="lg" mb={2}>
              AI WhatsApp Chatbot
            </Heading>
            <Text fontSize="14px" color={textColorSub} maxW="800px">
              Automate WhatsApp Business with AI-powered conversation pathways that connect directly through Meta Business Manager.
              Build intelligent flows, manage multiple numbers, and optimise engagement with real-time analytics.
            </Text>

            {phoneNumbers.length > 0 && (
              <Text mt={3} fontSize="md">
                <Text as="span" fontWeight="bold">Purchased numbers:</Text>{' '}
                <Text as="span">{purchasedCount}</Text>
              </Text>
            )}
          </Box>

          {phoneNumbers.length > 0 && (
            <Box flexShrink={0}>
              <Button
                as={Link}
                href={`${FLOW_BASE_URL}/authenticateHustle?token=${userInfo?.token ?? ''}`}
                colorScheme="blue"
                size={{ base: 'sm', md: 'sm' }}
                isExternal
                leftIcon={<Icon as={FaWhatsapp} />}
                whiteSpace="nowrap"
              >
                Connect WhatsApp
              </Button>
            </Box>
          )}
        </Flex>

        {/* Body */}
        {isFetching ? (
          <Flex direction="column" gap={2} align="center" justify="center" h="60vh">
            <Spinner />
            <Text>Loading…</Text>
          </Flex>
        ) : isError ? (
          <Flex direction="column" gap={3} align="center" justify="center" h="60vh">
            <Text fontSize="sm" textAlign="center" maxW="600px">
              We couldn’t fetch your WhatsApp number. To get started, click the button below and create an account using the same email ID you’re currently logged in with.
            </Text>
            <Button
              as={Link}
              href={`${FLOW_BASE_URL}/authenticateHustle?token=${userInfo?.token ?? ''}`}
              colorScheme="blue"
              size="sm"
              isExternal
              width="auto"
              leftIcon={<Icon as={FaWhatsapp} />}
            >
              Connect WhatsApp
            </Button>
          </Flex>
        ) : phoneNumbers.length === 0 ? (
          <Flex direction="column" gap={3} align="center" justify="center" h="60vh">
            <Text>No WhatsApp numbers found for this account.</Text>
            <Text fontSize="sm" color={subtle} mt={1}>
              Click “Connect WhatsApp” to add or import your numbers.
            </Text>
            <Button
              as={Link}
              mt={2}
              href={`${FLOW_BASE_URL}/authenticateHustle?token=${userInfo?.token ?? ''}`}
              colorScheme="blue"
              size="sm"
              isExternal
              width="auto"
              leftIcon={<Icon as={FaWhatsapp} />}
            >
              Connect WhatsApp
            </Button>
          </Flex>
        ) : (
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
            {phoneNumbers.map((num, idx) => (
              <Flex
                key={`${num}-${idx}`}
                borderWidth="1px"
                borderColor={cardBorder}
                rounded="xl"
                p={4}
                gap={3}
                align="center"
                bg={bgColor}
              >
                <Box
                  w="42px"
                  h="42px"
                  rounded="full"
                  display="grid"
                  placeItems="center"
                  borderWidth="1px"
                  borderColor={cardBorder}
                >
                  <Icon as={FaWhatsapp} boxSize={5} />
                </Box>

                <VStack align="start" spacing={0} flex="1" minW={0}>
                  <Text fontWeight="semibold" noOfLines={1}>
                    {num}
                  </Text>
                </VStack>

                {copiedNum === num ? (
                  <Flex align="center" gap={1} color="green.500" minW="fit-content">
                    <CheckIcon boxSize={4} />
                    <Text fontSize="xs">Copied</Text>
                  </Flex>
                ) : (
                  <Tooltip label="Copy number" fontSize={'12px'}>
                    <IconButton
                      aria-label="Copy number"
                      size="sm"
                      variant="ghost"
                      icon={<FiCopy />}
                      onClick={() => copyToClipboard(num)}
                    />
                  </Tooltip>
                )}
              </Flex>
            ))}
          </SimpleGrid>
        )}
      </Box>
    </SidebarWithHeader>
  );
}

export default WhatsAppBot;
